<template>
  <slot></slot>
  <modal :id="modalId" :title="modalTitle" @shown="init">

    <div>
      <div v-if="error">
        <div class="alert alert-danger">
          {{ error }}
        </div>
      </div>

      <!-- Name -->
      <div class="form-group row my-3">
        <label for="name" class="col-form-label col-sm-2">Name:</label>
        <div class="col-sm-10">
          <input v-model="name" type="text" class="form-control form-control-sm" :class="{'is-invalid': errors && errors.name}" placeholder="Enter a smartlist name...">
          <div v-if="errors && errors.name" class="small text-danger">{{ errors.name[0] }}</div>
        </div>
      </div>

      <!-- Fields -->
      <div class="form-group row my-3">
        <label for="fields" class="col-form-label col-sm-2">Fields:</label>
        <div class="col-sm-10">

          <div v-if="addFieldsMode === 'hidden'" class="row">
            <div class="col-sm-6">
              <button @click="addFieldsMode = 'native'" class="btn btn-secondary btn-sm btn-block"><i class="fa fa-tags"></i> Add native fields</button>
            </div>
            <div class="col-sm-6">
              <button @click="addFieldsMode = 'custom'" class="btn btn-secondary btn-sm btn-block"><i class="fa fa-cogs"></i> Add custom fields</button>
            </div>
          </div>

          <div v-if="addFieldsMode === 'native'">
            <div v-if="unusedNativeFieldConfigs.length === 0" class="alert alert-info text-center mb-0">
              All native fields are added.
            </div>

            <template v-else>
              <template v-for="fieldType in unusedNativeFieldConfigs">
                <button @click="switchNativeFieldTypeSelection(fieldType)" class="btn btn-success btn-sm mr-1 mb-1">
                  <i class="fa fa-fw fa-plus-circle"></i> {{ fieldType.name }}
                </button>
              </template>
            </template>

            <div class="text-center mt-3">
              <button @click="addFieldsMode = 'hidden'" class="btn btn-sm btn-secondary btn-block"><i class="fa fa-fw fa-ban"></i> Cancel</button>
            </div>
          </div>

          <div v-if="addFieldsMode === 'custom'">

            <template v-if="!newField.type">
              <template v-for="fieldType in customFieldConfigs">
                <button @click="newField.type = fieldType.id" class="btn btn-success btn-sm mr-1 mb-1">
                  <i class="fa fa-fw fa-cog"></i> {{ fieldType.name }}
                </button>
              </template>
            </template>

            <div v-else class="alert alert-success mb-0">

              <h3 class="mb-3 text-center">
                {{ newFieldType.name }}
              </h3>

              <div class="mb-1">
                <input v-model="newField.name" type="text" class="form-control form-control-sm" placeholder="Enter field name...">
                <span v-if="!newField.isValid && newField.errors.name" class="small text-danger">{{ newField.errors.name }}</span>
              </div>

              <template v-if="newFieldHasSettings">
                <div class="mb-1">
                  <component ref="newFieldSettings" :is="newFieldSettingsComponent" v-model:value="newField.params" v-model:valid="newField.isValidParams" :is-new="true"></component>
                </div>
              </template>

              <div class="mt-4 text-center">
                <button @click="clearFieldForm('newField')" class="btn btn-sm btn-default m-1"><i class="fa fa-fw fa-ban"></i> Cancel</button>
                <button @click="addCustomField()" class="btn btn-primary btn-sm m-1"><i class="fa fa-fw fa-plus-circle"></i> Add field to the list</button>
              </div>

            </div>

            <div class="text-center mt-3">
              <button @click="addFieldsMode = 'hidden'" class="btn btn-sm btn-secondary btn-block"><i class="fa fa-fw fa-ban"></i> Cancel</button>
            </div>
          </div>

          <table v-if="fields.length > 0" class="table mt-3">
            <thead>
            <tr>
              <th>Order</th>
              <th>Name</th>
              <th>Type</th>
              <th class="w-50s"></th>
              <th class="w-50s"></th>
            </tr>
            </thead>

            <Sortable
              :list="fields"
              item-key="id"
              tag="tbody"
              @end="dragEnd">
              <template #item="{element, index}">
                <tr :data-key="element.id">

                  <!-- Field deletion confirmation view -->
                  <td v-if="confirmDeleteCustomFieldId === element.id" colspan="5">
                    <div class="alert alert-danger text-center mb-0">
                      <h4 class="mb-4">"{{ element.name }}" field deletion confirmation</h4>

                      You are about to delete "{{ element.name }}" field.
                      If the smartlist has any saved records for this field, they will all be lost.
                      This action cannot be undone.

                      <div class="mt-4">
                        <button @click="confirmDeleteCustomFieldId = 0" class="btn btn-sm btn-default m-1"><i class="fa fa-fw fa-ban"></i> Cancel</button>
                        <button @click="deleteCustomField(element)" class="btn btn-sm btn-danger m-1"><i class="fa fa-fw fa-remove"></i> Confirm field deletion</button>
                      </div>
                    </div>
                  </td>

                  <!-- Field edit view -->
                  <td v-else-if="editField.id === element.id" colspan="5">
                    <div class="alert alert-info mb-0">

                      <h3 class="mb-3 text-center">
                        {{ editFieldType.name }}
                      </h3>

                      <div class="mb-1">
                        <input v-model="editField.name" type="text" class="form-control form-control-sm" placeholder="Enter field name...">
                        <span v-if="!editField.isValid && editField.errors.name" class="small text-danger">{{ editField.errors.name }}</span>
                      </div>

                      <template v-if="editFieldHasSettings">
                        <div class="mb-1">
                          <component ref="editFieldSettings" :is="editFieldSettingsComponent" v-model:value="editField.params" v-model:valid="editField.isValidParams" :existing-field="existingCustomFieldIds.filter(fid => fid === editField.id).length > 0"></component>
                        </div>
                      </template>

                      <div class="mt-4 text-center">
                        <button @click="clearFieldForm('editField')" class="btn btn-sm btn-default m-1"><i class="fa fa-fw fa-ban"></i> Cancel</button>
                        <button @click="saveEditField" class="btn btn-sm btn-primary m-1"><i class="fa fa-fw fa-save"></i> Save</button>
                      </div>
                    </div>
                  </td>

                  <!-- Field information view -->
                  <template v-else>
                    <td class="w-50s grabbable">
                      <i class="fa fa-arrows"></i>
                    </td>

                    <td>
                      {{ element.name }}
                    </td>

                    <td>
                      <template v-if="element.is_native"><i class="fa fa-tag"></i> Native field</template>
                      <template v-else><i class="fa fa-cog"></i> {{ customFieldConfigs.find(t => t.id === element.type).name }}</template>
                    </td>

                    <!-- Edit field action -->
                    <td>
                      <template v-if="!element.is_native">
                        <button @click="showCustomFieldEditForm(element)" class="btn btn-sm btn-secondary"><i class="fa fa-fw fa-edit"></i></button>
                      </template>
                    </td>

                    <!-- Delete field action -->
                    <td>
                      <button v-if="!element.is_native" @click="confirmOrDeleteCustomField(element)" class="btn btn-sm btn-secondary"><i class="fa fa-fw fa-remove"></i></button>
                      <button v-else @click="switchNativeFieldTypeSelection(nativeFieldConfigs.filter(c => c.id === element.type)[0])" class="btn btn-sm btn-secondary"><i class="fa fa-fw fa-minus-circle"></i></button>
                    </td>
                  </template>
                </tr>
              </template>
            </Sortable>

          </table>

          <div v-if="errors && errors.fields" class="small text-danger">{{ errors.fields[0] }}</div>
        </div>
      </div>

      <!-- Pagination settings -->
      <div class="form-group row my-3">
        <label class="col-sm-2 col-form-label" for="page_size">Items per page:</label>
        <div class="col-sm-10">
          <select v-model="pageLimit" class="form-control" :class="{'is-invalid': errors && errors.page_limit}">
            <option v-for="limit in pageLimitOptions" :value="limit">{{ limit }}</option>
          </select>
          <div v-if="errors && errors.page_limit" class="small text-danger">{{ errors.page_limit[0] }}</div>
        </div>
      </div>

      <!-- Note settings -->
      <div class="form-group row my-3">
        <div class="col-sm-10 offset-sm-2">
          <label class="checkbox-inline">
            <input type="checkbox" v-model="showNote"> Display the most recent note in the list
          </label>
          <div v-if="errors && errors.show_note" class="small text-danger">{{ errors.show_note[0] }}</div>
        </div>
      </div>

    </div>

    <template v-slot:footer>
      <button type="button" class="btn btn-default" @click="hideModal"><i class="fa fa-fw fa-ban"></i> Cancel</button>

      <button type="button" class="btn btn-primary" @click="submitData()" :disabled="processingSubmit">
        <i class="fa fa-fw fa-save"></i> Save
        <i class="fa fa-spinner fa-spin" v-if="processingSubmit"></i>
      </button>
    </template>

  </modal>
</template>

<script>
  import { Sortable } from "sortablejs-vue3";
  import HasModal from "../mixins/HasModal";
  import SelectListSettings from "./widgets/settings/SelectListSettings.vue";

  export default {
    mixins: [HasModal],

    components: {
      select_list_settings: SelectListSettings,
      Sortable
    },

    props: {
      action: {type: String, required: true},
      modalId: {type: String, default: 'smartlistSettingsModal'},

      nativeFieldConfigs: {type: Array, default: function () { return [] }},
      customFieldConfigs: {type: Array, default: function () { return [] }},

      defaultFields: {type: Array, default: function () { return [] }},

      id: {type: Number, default: 0},
      initialName: {type: String, default: ''},
      initialFields: {type: Array, default: function () { return [] }},
      initialShowNote: {type: Boolean, default: true},
      initialPageLimit: {type: Number, default: 25},
    },

    /**
     * Variables (models).
     */
    data () {
      return {
        modalTitle: (this.id === 0) ? 'Create Smartlist' : 'Smartlist Settings',
        processingSubmit: false,
        error: '',
        errors: {},
        confirmDeleteCustomFieldId: 0,
        addFieldsMode: 'hidden',

        existingCustomFieldIds: _.cloneDeep(this.initialFields.filter(f => !f.is_native).map(f => f.id)),

        // Form fields.
        name: this.initialName,
        showNote: this.initialShowNote,
        pageLimit: this.initialPageLimit === 0 ? 'All' : this.initialPageLimit,
        pageLimitOptions: [10, 25, 50, 100, 250, 500, 750, 1000, 'All'],
        fields: (this.id === 0) ? this.defaultFields : this.initialFields,
        newField: {},
        editField: {},
      }
    },

    /**
     * Computed values
     */
    computed: {

      newFieldType() {
        return this.customFieldConfigs.find(t => t.id === this.newField.type) ?? null;
      },

      newFieldHasSettings() {
        if (this.newFieldType === null) return false;
        return this.newFieldType.widget_settings !== undefined;
      },

      newFieldSettingsComponent() {
        if (this.newFieldType === null) return null;
        return this.newFieldType.widget_settings;
      },

      editFieldType() {
        return this.customFieldConfigs.find(t => t.id === this.editField.type) ?? null;
      },

      editFieldHasSettings() {
        if (this.editField.type === undefined) return false;
        return this.customFieldConfigs.find(t => t.id === this.editField.type).widget_settings !== undefined;
      },

      editFieldSettingsComponent() {
        if (this.editField.type === undefined) return false;
        return this.customFieldConfigs.find(t => t.id === this.editField.type).widget_settings;
      },

      unusedNativeFieldConfigs() {
        return this.nativeFieldConfigs.filter(config => !this.isNativeFieldTypeSelected(config));
      },

    },

    /**
     * Functions (methods).
     */
    methods: {

      init() {
        this.name = this.initialName;
        this.fields = (this.id === 0) ? this.defaultFields : this.initialFields;
        this.showNote = this.initialShowNote;
        this.pageLimit = this.initialPageLimit === 0 ? 'All' : this.initialPageLimit;
        this.clearFieldForm('newField');
        this.clearFieldForm('editField');
        this.clearValidation();
        this.confirmDeleteCustomFieldId = 0;
        this.addFieldsMode = 'hidden';
      },

      clearFieldForm(fieldFormType) {
        this[fieldFormType] = {
          id: 0,
          name: '',
          type: '',
          params: {},

          isValid: true,
          isValidParams: true,
          errors: {},
        };
      },

      clearValidation() {
        this.errors = {};
        this.error = '';
      },

      dragEnd(e) {
        const id = e.item.dataset.key;
        const field = this.fields.find(f => f.id === id);
        this.fields.splice(e.oldIndex, 1);
        this.fields.splice(e.newIndex, 0, field);
      },

      showCustomFieldEditForm(field) {
        this.editField = _.cloneDeep(field);
        this.editField.isValid = true;
        this.editField.isValidParams = true;
        this.editField.errors = {};
      },

      saveEditField() {
        if (!this.validateFieldForm('editField', this.editFieldHasSettings ? this.$refs.editFieldSettings : null)) return;

        const fieldIndex = this.fields.findIndex(cf => (cf.id === this.editField.id) && !cf.is_native);
        delete this.editField.isValid;
        delete this.editField.isValidParams;
        delete this.editField.errors;
        this.fields[fieldIndex] = _.cloneDeep(this.editField);
        this.clearFieldForm('editField');
      },

      addCustomField() {
        if (!this.validateFieldForm('newField', this.newFieldHasSettings ? this.$refs.newFieldSettings : null)) return;

        this.fields.push({
          id: this.ensureUniqueCustomFieldId(this.newField.name),
          name: this.newField.name,
          type: this.newField.type,
          params: this.newField.params,
          is_native: false,
        });
        this.clearFieldForm('newField');
        this.clearFieldForm('editField');
      },

      validateFieldForm(fieldFormType, settingsRef = null) {
        this[fieldFormType].errors = {};
        if (this[fieldFormType].name.trim().length === 0) this[fieldFormType].errors.name = 'Field name is required';
        this[fieldFormType].isValidParams = true;
        if (this[fieldFormType + 'HasSettings']) settingsRef.validate();
        this[fieldFormType].isValid = this[fieldFormType].isValidParams && Object.keys(this[fieldFormType].errors).length === 0;
        return this[fieldFormType].isValid;
      },

      isNativeFieldTypeSelected(fieldType) {
        return this.fields.filter(f => f.is_native).find(nf => nf.type === fieldType.id) !== undefined;
      },

      switchNativeFieldTypeSelection(fieldType) {
        if (!this.isNativeFieldTypeSelected(fieldType)) {
          this.fields.push({
            id: fieldType.id,
            name: fieldType.name,
            type: fieldType.id,
            is_native: true,
          });
          return;
        }
        this.deleteNativeFieldByType(fieldType.id);
      },

      deleteNativeFieldByType(fieldType) {
        this.fields = this.fields.filter(f => !f.is_native || f.is_native && (f.type !== fieldType));
      },

      ensureUniqueCustomFieldId(fieldLabel) {
        const snakeCase = string => {
          return string.replace(/\W+/g, " ")
            .split(/ |\B(?=[A-Z])/)
            .map(word => word.toLowerCase())
            .join('_');
        };
        let baseId = snakeCase(fieldLabel);
        let id = baseId;
        let index = 1;
        const customFields = this.fields.filter(f => f.is_native);
        if (customFields.length === 0) return id;

        while (true) {
          if (customFields.find(cf => cf.id === id) === undefined) break;
          index++;
          id = baseId + '_' + index;
        }

        return id;
      },

      confirmOrDeleteCustomField(field) {
        if (this.existingCustomFieldIds.filter(fid => fid === field.id).length > 0) {
          this.confirmDeleteCustomFieldId = field.id;
          return;
        }
        this.deleteCustomField(field);
      },

      deleteCustomField(field) {
        this.fields = this.fields.filter(f => f.is_native || !f.is_native && (f.id !== field.id));
      },

      submitData() {
        this.clearValidation();
        this.processingSubmit = true;
        let formData = this.formData();
        axios.post(this.action, formData, formData)
          .then(response => {
            this.hideModal();
            window.location.href = response.data.redirect;
          })
          .catch(error =>{
            this.handleErrors(error, 'Unable to process request.')
          })
          .then(() =>{
            this.processingSubmit = false;
          });
      },

      formData() {
        const formData = new FormData();
        formData.append('_token', this.$csrf_token);
        if (this.id !== 0) formData.append('_method', 'PATCH');

        formData.append('name', this.name);
        formData.append('fields', JSON.stringify(this.fields));
        formData.append('show_note', this.showNote ? 1 : 0);
        formData.append('page_limit', this.pageLimit === 'All' ? 0 : this.pageLimit);

        return formData;
      },

      handleErrors(errorObj, defaultMessage) {
        if (errorObj.response.status === 422) {
          this.errors = errorObj.response.data.errors || {};
          let errorMessages = [];
          Object.keys(this.errors).forEach(field => {
            errorMessages.push(this.errors[field]);
          })
          this.error = errorMessages.join(' ');
        } else {
          this.error = defaultMessage;
        }
      },

    },
  }
</script>

<style scoped>
.grabbable {
  cursor: move; /* fallback if grab cursor is unsupported */
  cursor: grab;
  cursor: -moz-grab;
  cursor: -webkit-grab;
}

/* (Optional) Apply a "closed-hand" cursor during drag operation. */
.grabbable:active {
  cursor: grabbing;
  cursor: -moz-grabbing;
  cursor: -webkit-grabbing;
}
</style>
